import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import QuestionDeleteDialog from "../components/dialog/QuestionDeleteDialog";
import QuestionDetail from "../components/QuestionDetail";
import { getQuestionList, getQuestionDetail } from "../services/callApi";
import { API_QUERY_KEYS, invalidateApiQueries } from "../utils/apiQueryCache";
import {
  MANAGEMENT_ACTION_CELL_SX,
  MANAGEMENT_BODY_CELL_SX,
  MANAGEMENT_HEADER_CELL_SX,
  MANAGEMENT_ROWS_PER_PAGE,
  MANAGEMENT_SPACER_BODY_CELL_SX,
  MANAGEMENT_SPACER_HEADER_CELL_SX,
  MANAGEMENT_TABLE_SX,
  ManagementEmptyState,
  ManagementIconActionButton,
  ManagementListPage,
  ManagementListPagination,
  ManagementTableContainer,
  ManagementTruncatedText,
} from "./common/ManagementListLayout";

export const QUESTION_UPDATED_AT_COLUMN_WIDTH = 220;

const QuestionList = () => {
  const [loading, setLoading] = useState(true);
  const [isSelect, setIsSelect] = useState(false);
  const [problemData, setProblemData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [page, setPage] = useState(0);

  const fetchQuestionList = useCallback(async () => {
    try {
      const response = await getQuestionList();
      setProblemData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error(error);
      alert("問題一覧の取得に失敗しました。\nページの再読み込みをしてください。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestionList();
  }, [fetchQuestionList]);

  const refreshQuestionList = useCallback(async () => {
    invalidateApiQueries(API_QUERY_KEYS.questionList);
    await fetchQuestionList();
  }, [fetchQuestionList]);

  const currentItems = problemData.slice(
    page * MANAGEMENT_ROWS_PER_PAGE,
    page * MANAGEMENT_ROWS_PER_PAGE + MANAGEMENT_ROWS_PER_PAGE
  );

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(problemData.length / MANAGEMENT_ROWS_PER_PAGE) - 1);
    setPage((currentPage) => Math.min(currentPage, lastPage));
  }, [problemData.length]);

  const fetchQuestionDetail = async (id) => {
    try {
      const response = await getQuestionDetail(id);
      setDetailData(response?.[0] ?? null);
    } catch (error) {
      console.error("問題詳細の取得に失敗しました。", error);
      setDetailData(null);
      alert("問題詳細の取得に失敗しました。\nページの再読み込みをしてください。");
    }
  };

  const handleDetailClick = (id) => {
    fetchQuestionDetail(id);
    setIsSelect(true);
  };

  if (loading) {
    return (
      <ManagementListPage title="問題一覧" count={0}>
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 240 }}>
          <CircularProgress />
        </Box>
      </ManagementListPage>
    );
  }

  return (
    <Box>
      <Box hidden={isSelect}>
        <ManagementListPage title="問題一覧" count={problemData.length}>
          {problemData.length === 0 ? (
            <ManagementEmptyState>登録されている問題はありません。</ManagementEmptyState>
          ) : (
            <ManagementTableContainer>
              <Table sx={{ ...MANAGEMENT_TABLE_SX, minWidth: 1020 }} aria-label="問題一覧">
                <colgroup>
                  <col style={{ width: 100 }} />
                  <col style={{ width: QUESTION_UPDATED_AT_COLUMN_WIDTH }} />
                  <col style={{ width: 220 }} />
                  <col style={{ width: 220 }} />
                  <col style={{ width: 88 }} />
                  <col />
                  <col style={{ width: 72 }} />
                  <col style={{ width: 72 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>問題番号</TableCell>
                    <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>変更日時</TableCell>
                    <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>問題画像名</TableCell>
                    <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>回答画像名</TableCell>
                    <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>正答</TableCell>
                    <TableCell aria-hidden="true" sx={MANAGEMENT_SPACER_HEADER_CELL_SX} />
                    <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>詳細</TableCell>
                    <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>削除</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentItems.map((problem) => (
                    <TableRow key={problem.questionId} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                        {`${problem.questionPart}-${problem.questionId}`}
                      </TableCell>
                      <TableCell sx={{ ...MANAGEMENT_BODY_CELL_SX, whiteSpace: "nowrap" }}>
                        {problem.updatedAt ?? "－"}
                      </TableCell>
                      <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                        <ManagementTruncatedText>{problem.questionImageName}</ManagementTruncatedText>
                      </TableCell>
                      <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                        <ManagementTruncatedText>{problem.correctImageName}</ManagementTruncatedText>
                      </TableCell>
                      <TableCell align="center" sx={MANAGEMENT_BODY_CELL_SX}>
                        {problem.correctAnswer}
                      </TableCell>
                      <TableCell aria-hidden="true" sx={MANAGEMENT_SPACER_BODY_CELL_SX} />
                      <TableCell align="center" sx={MANAGEMENT_ACTION_CELL_SX}>
                        <ManagementIconActionButton
                          ariaLabel={`${problem.questionPart}-${problem.questionId}を詳細表示`}
                          tooltip="詳細"
                          onClick={() => handleDetailClick(problem.questionId)}
                        >
                          <EditIcon fontSize="small" />
                        </ManagementIconActionButton>
                      </TableCell>
                      <TableCell align="center" sx={MANAGEMENT_ACTION_CELL_SX}>
                        <QuestionDeleteDialog problem={problem} onDeleted={refreshQuestionList} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <ManagementListPagination
                count={problemData.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
              />
            </ManagementTableContainer>
          )}
        </ManagementListPage>
      </Box>
      <Box hidden={!isSelect}>
        <QuestionDetail
          detailData={detailData}
          onBack={() => setIsSelect(false)}
          onUpdated={refreshQuestionList}
        />
      </Box>
    </Box>
  );
};

export default QuestionList;
